'use client';
import { BioCoverImage } from './BioCoverImage';
import type { BioFollowing } from '@/lib/link-in-bio/connected-accounts';
import './bio-motion.css';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { IconArrowUpRight, IconLink, IconShare2, IconSparkles } from '@tabler/icons-react';
import { Avatar, Button } from '@mantine/core';
import { type BioBlock, type BioDocument } from '@/lib/link-in-bio/model';
import { DEFAULT_KIT_ACCENT } from '@/lib/media-kit/accent-palette';
import { BIO_ACCENTS } from '@/lib/link-in-bio/accent-palette';
import s from './bio.module.css';
import { BioCollection, BioMessage, BioRichLink } from './BioRichBlocks';
import { IconRosetteDiscountCheckFilled } from '@tabler/icons-react';
import { createBioCollector } from '@/lib/link-in-bio/analytics-collector';
import { BioBlockIcon } from './BioBlockIcon';
import { presentBioBlocks } from '@/lib/link-in-bio/presentation';
import { bioDescription } from '@/lib/link-in-bio/discovery';
import { bioBadgeHref } from '@/lib/link-in-bio/routes';
import { withBrandfetchClientId } from '@/lib/brands/logo';

function BioBranding({ preview, slug }: { preview: boolean; slug: string }) {
  const label = (
    <>
      <span>
        Made with <strong>OwlScran</strong>
      </span>
      <span className={s.brandingDivider} aria-hidden>
        ·
      </span>
      <span className={s.brandingCta}>
        Get your free link in bio
        <IconArrowUpRight size={13} aria-hidden />
      </span>
    </>
  );
  if (preview)
    return (
      <span className={s.branding} data-bio-branding>
        {label}
      </span>
    );
  // Same tab: visitors land on the free link-in-bio page with the badge's campaign tags.
  return (
    <a className={s.branding} data-bio-branding href={bioBadgeHref(slug)}>
      {label}
    </a>
  );
}

const asOfFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const VERIFIED_LABEL = 'Connected social accounts verified by OwlScran';

const HIDE_DELAY_MS = 150;

/**
 * Tooltip on hover or keyboard focus; a tap or click pins it open until tapped again,
 * Escape or an outside tap. The native popover keeps the note in the server HTML, and it
 * sits outside the heading so the name stays clean. Browsers without popover support
 * keep the icon and its accessible description; the note stays hidden.
 */
function useVerifiedTooltip(noteId: string) {
  const pinned = useRef(false);
  const peeking = useRef(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const note = () => {
    const element = document.getElementById(noteId);
    return element && 'showPopover' in element ? element : null;
  };
  const isOpen = () => Boolean(note()?.matches(':popover-open'));
  const cancelHide = () => clearTimeout(hideTimer.current);
  const peek = () => {
    cancelHide();
    if (isOpen()) return;
    peeking.current = true;
    note()?.showPopover();
  };
  // A short delay lets the pointer move onto the note without it disappearing.
  const unpeek = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => {
      if (!pinned.current && isOpen()) note()?.hidePopover();
    }, HIDE_DELAY_MS);
  };
  useEffect(() => {
    const element = document.getElementById(noteId);
    if (!element || !('showPopover' in element)) return;
    // A tap before hydration opens the popover natively; treat it as pinned.
    pinned.current = element.matches(':popover-open');
    const sync = (event: Event) => {
      // Opens that did not come from hover or focus are taps, clicks or key presses.
      pinned.current = (event as ToggleEvent).newState === 'open' && !peeking.current;
      peeking.current = false;
    };
    element.addEventListener('toggle', sync);
    return () => {
      element.removeEventListener('toggle', sync);
      clearTimeout(hideTimer.current);
    };
  }, [noteId]);
  const mouse = (event: PointerEvent<HTMLElement>) => event.pointerType === 'mouse';
  return {
    button: {
      onPointerEnter: (event: PointerEvent<HTMLElement>) => mouse(event) && peek(),
      onPointerLeave: (event: PointerEvent<HTMLElement>) => mouse(event) && unpeek(),
      onFocus: (event: FocusEvent<HTMLElement>) =>
        event.currentTarget.matches(':focus-visible') && peek(),
      onBlur: unpeek,
      onClick: (event: MouseEvent<HTMLElement>) => {
        // Already showing from hover or focus: pin it rather than letting the toggle close it.
        if (isOpen() && !pinned.current) {
          event.preventDefault();
          pinned.current = true;
        }
      },
    },
    note: {
      onPointerEnter: (event: PointerEvent<HTMLElement>) => mouse(event) && cancelHide(),
      onPointerLeave: (event: PointerEvent<HTMLElement>) => mouse(event) && unpeek(),
    },
  };
}

function BioVerifiedButton({
  noteId,
  handlers,
}: {
  noteId: string;
  handlers: ReturnType<typeof useVerifiedTooltip>['button'];
}) {
  return (
    <button
      type="button"
      className={s.verifiedButton}
      popoverTarget={noteId}
      aria-label={VERIFIED_LABEL}
      aria-describedby={noteId}
      style={{ anchorName: `--${noteId}` } as CSSProperties}
      {...handlers}
    >
      <IconRosetteDiscountCheckFilled className={s.verified} size={22} aria-hidden />
    </button>
  );
}

function BioVerifiedNote({
  noteId,
  asOf,
  handlers,
}: {
  noteId: string;
  asOf?: string;
  handlers: ReturnType<typeof useVerifiedTooltip>['note'];
}) {
  return (
    <div
      id={noteId}
      popover="auto"
      role="tooltip"
      className={s.verifiedNote}
      style={{ positionAnchor: `--${noteId}` } as CSSProperties}
      {...handlers}
    >
      <strong>Verified by OwlScran</strong>
      <span>The social icons at the top of this page link to accounts connected to OwlScran.</span>
      {asOf && (
        <span>
          Follower numbers come from connected accounts and can’t be edited. Updated{' '}
          {asOfFormat.format(new Date(`${asOf}T12:00:00Z`))}.
        </span>
      )}
    </div>
  );
}

function BioLinkBody({ block, heading }: { block: BioBlock; heading: boolean }) {
  const [failedImage, setFailedImage] = useState<string>();
  const image = withBrandfetchClientId(block.icon || block.image);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const element = imageRef.current;
    if (element?.complete && element.naturalWidth === 0) setFailedImage(image);
  }, [image]);
  return (
    <>
      {!block.featured && (
        <span
          className={s.publicGlyph}
          data-logo={Boolean(image && failedImage !== image) || undefined}
          aria-hidden
        >
          {image && failedImage !== image ? (
            <img
              ref={imageRef}
              src={image}
              alt=""
              width={32}
              height={32}
              referrerPolicy="no-referrer"
              className={s.websiteLogo}
              onError={() => setFailedImage(image)}
            />
          ) : (
            <BioBlockIcon block={block} />
          )}
        </span>
      )}
      <span>
        <strong role={heading ? 'heading' : undefined} aria-level={heading ? 2 : undefined}>
          {block.title}
        </strong>
        {block.description && <span className={s.linkDescription}>{block.description}</span>}
      </span>
      <IconArrowUpRight size={22} aria-hidden />
    </>
  );
}

function BioBlockRow({
  block,
  heading,
  preview,
  track,
  slug,
}: {
  block: BioBlock;
  heading: boolean;
  preview: boolean;
  slug: string;
  track: (blockId: string) => void;
}) {
  if (block.type === 'collection')
    return <BioCollection block={block} preview={preview} track={track} />;
  if (block.type === 'message') return <BioMessage block={block} slug={slug} preview={preview} />;
  if (block.type === 'link' && block.display && block.display !== 'compact')
    return <BioRichLink block={block} preview={preview} track={track} />;
  if (block.type === 'heading') {
    if (preview) return <p className={s.sectionHeading}>{block.title}</p>;
    return <h2 className={s.sectionHeading}>{block.title}</h2>;
  }
  if (block.type === 'text') {
    return (
      <div className={s.note}>
        {preview ? <p className={s.noteTitle}>{block.title}</p> : <h2>{block.title}</h2>}
        <p>{block.description}</p>
      </div>
    );
  }
  if (preview) {
    return (
      <div
        className={block.featured ? s.featuredLink : s.publicLink}
        data-bio-featured={block.featured || undefined}
      >
        <BioLinkBody block={block} heading={false} />
      </div>
    );
  }
  return (
    <a
      className={block.featured ? s.featuredLink : s.publicLink}
      href={block.url}
      rel="ugc noopener noreferrer"
      onClick={() => track(block.id)}
      onAuxClick={(event) => {
        if (event.button === 1) track(block.id);
      }}
    >
      <BioLinkBody block={block} heading={heading} />
    </a>
  );
}

function BioIdentity({
  document,
  Title,
  preview,
  verified,
  following,
}: {
  following?: BioFollowing | null;
  verified: boolean;
  document: BioDocument;
  Title: 'h1' | 'h2' | 'p';
  preview: boolean;
}) {
  const verifiedNoteId = `bio-verified-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const verifiedTooltip = useVerifiedTooltip(verifiedNoteId);
  const body = (
    <>
      <Avatar
        imageProps={{ referrerPolicy: 'no-referrer' }}
        src={document.avatar || undefined}
        alt={document.name}
        name={document.name}
        size={56}
        radius="50%"
        className={s.avatar}
      />
      <Title className={s.creatorName} data-bio-name>
        {document.name || 'Your name'}
        {verified &&
          (preview ? (
            <IconRosetteDiscountCheckFilled
              className={s.verified}
              size={22}
              aria-label={VERIFIED_LABEL}
              role="img"
            />
          ) : (
            <BioVerifiedButton noteId={verifiedNoteId} handlers={verifiedTooltip.button} />
          ))}
      </Title>
      {verified && !preview && (
        <BioVerifiedNote
          noteId={verifiedNoteId}
          handlers={verifiedTooltip.note}
          asOf={document.showFollowerCount && following ? following.asOf : undefined}
        />
      )}
      {(document.bio || !preview) && <p>{bioDescription(document)}</p>}
      {document.showFollowerCount && following && (
        <div
          className={s.following}
          title={`Updated ${new Date(`${following.asOf}T12:00:00Z`).toLocaleDateString('en-GB')}. Added across platforms; some people may follow on more than one.`}
        >
          <IconSparkles size={20} aria-hidden />
          <span>
            <strong
              key={following.total}
              className="t-digit-group is-animating"
              aria-label={`${following.total.toLocaleString('en-GB')} followers`}
            >
              {new Intl.NumberFormat('en-GB', {
                notation: 'compact',
                maximumFractionDigits: 1,
                roundingMode: 'trunc',
              })
                .format(following.total)
                .split('')
                .map((digit, index, digits) => (
                  <span
                    aria-hidden
                    key={index}
                    className="t-digit"
                    data-stagger={
                      index >= digits.length - 2 ? index - digits.length + 3 : undefined
                    }
                  >
                    {digit}
                  </span>
                ))}
            </strong>
            <span> followers & counting</span>
            <small>
              Across {following.platforms} connected{' '}
              {following.platforms === 1 ? 'platform' : 'platforms'}
            </small>
          </span>
        </div>
      )}
      {!!document.socials?.length && (
        <nav className={s.socials} aria-label="Social profiles">
          {document.socials.map((social) => (
            <a
              key={social.platform}
              href={preview ? undefined : social.url}
              aria-label={social.platform}
              target="_blank"
              rel="ugc noopener noreferrer"
            >
              <BioBlockIcon block={{ type: 'link', url: social.url }} platform={social.platform} />
            </a>
          ))}
        </nav>
      )}
    </>
  );
  if (preview)
    return (
      <div className={s.identity} data-avatar-shape={document.avatarShape ?? 'circle'}>
        {body}
      </div>
    );
  return (
    <header className={s.identity} data-avatar-shape={document.avatarShape ?? 'circle'}>
      {body}
    </header>
  );
}

export function BioPage({
  document,
  slug,
  preview = false,
  headingAs: Title = preview ? 'h2' : 'h1',
  useSiteAccent = false,
  verified = false,
  following,
}: {
  document: BioDocument;
  slug: string;
  preview?: boolean;
  headingAs?: 'h1' | 'h2' | 'p';
  /** Marketing previews can inherit the viewer's chosen site accent. */
  useSiteAccent?: boolean;
  verified?: boolean;
  following?: BioFollowing | null;
}) {
  const load = useRef<{ slug: string; track: ReturnType<typeof createBioCollector> } | null>(null);
  const accent = BIO_ACCENTS.find((colour) => colour.hex === document.accent) ?? DEFAULT_KIT_ACCENT;
  const track = useCallback(
    (blockId: string) => {
      if (preview) return;
      if (!load.current || load.current.slug !== slug)
        load.current = { slug, track: createBioCollector(slug) };
      load.current.track(blockId);
    },
    [preview, slug],
  );
  useEffect(() => {
    track('view');
    // A fresh mount gets a load ID; effect replays and repeated link clicks do not inflate totals.
  }, [track]);
  return (
    <div
      className={s.publicPage}
      data-appearance={document.appearance}
      data-preview={preview || undefined}
      style={
        {
          '--bio-accent': preview && useSiteAccent ? 'rgb(var(--brand-rgb))' : accent.hex,
          '--bio-accent-ink': '#0a0a0a',
        } as CSSProperties
      }
    >
      <div className={s.publicInner} data-cover={Boolean(document.cover) || undefined}>
        {document.cover && (
          <BioCoverImage
            className={s.heroCover}
            src={document.cover}
            position={document.coverPosition}
            priority={!preview}
          />
        )}
        {!preview && (
          <Button
            variant="subtle"
            className={s.share}
            aria-label="Share this page"
            onClick={async () => {
              try {
                if (navigator.share)
                  await navigator.share({
                    title: document.name,
                    url: `${window.location.origin}/bio/${slug}`,
                  });
                else await navigator.clipboard.writeText(`${window.location.origin}/bio/${slug}`);
              } catch {
                /* Native sharing can be cancelled. */
              }
            }}
          >
            <IconShare2 size={20} />
          </Button>
        )}
        <BioIdentity
          document={document}
          Title={Title}
          preview={preview}
          verified={verified}
          following={following}
        />
        <div className={s.links} data-bio-links>
          {presentBioBlocks(document.blocks).map(({ block, heading }) => (
            <BioBlockRow
              key={block.id}
              block={block}
              slug={slug}
              heading={heading}
              preview={preview}
              track={track}
            />
          ))}
          {preview && !document.blocks.some((b) => b.visible) && (
            <div className={s.previewEmpty}>
              <IconLink size={24} />
              <p>Your links will appear here.</p>
            </div>
          )}
        </div>
        {document.showBranding && <BioBranding preview={preview} slug={slug} />}
      </div>
    </div>
  );
}
